import { parsePersonConnect } from './personConnect.js';
import { pppToolLabel } from './ppp.js';
/** User-facing labels for observed tool calls. Raw summaries may contain arguments or identifiers. */
const TOOL_LABELS = {
    list_my_tasks: 'Checking your tasks', list_tasks: 'Checking tasks', get_task: 'Reading a task',
    search_tasks: 'Searching tasks', list_projects: 'Checking projects', get_project: 'Reading a project',
    list_events: 'Checking your calendar', get_events: 'Checking your calendar',
    list_calendar_events: 'Checking your calendar', get_calendar_events: 'Checking your calendar',
    calendar_view: 'Checking your calendar', get_calendar_view: 'Checking your calendar',
    search_mail: 'Searching email', search_emails: 'Searching email', list_messages: 'Checking messages',
    search_messages: 'Searching messages', get_message: 'Reading a message',
    m365_mail_search: 'Searching your email', m365_mail_read: 'Reading an email',
    m365_chat_list: 'Checking your Teams chats', m365_chat_read: 'Reading a Teams chat',
    m365_calendar_list: 'Checking your calendar', m365_calendar_read: 'Reading a calendar event',
    list_mail: 'Checking email', list_emails: 'Checking email', get_email: 'Reading email', read_email: 'Reading email',
    search_teams: 'Searching Teams', search_teams_messages: 'Searching Teams', list_chats: 'Checking chats',
    list_chat_messages: 'Reading chat messages', get_chat_messages: 'Reading chat messages',
    search_files: 'Finding files', list_files: 'Checking files', read_file: 'Reading a file',
    list_documents: 'Finding documents', get_document: 'Reading a document',
    get_markups: 'Reading drawing comments', list_markups: 'Reading drawing comments',
    list_freshness_alerts: 'Checking freshness alerts', search_prompts: 'Searching prompts', get_prompt_detail: 'Reading a prompt',
    workspace_list: 'Checking task files', workspace_read: 'Reading a task file',
    workspace_read_text: 'Reading a task file', workspace_write_text: 'Creating a task file',
    workspace_write: 'Creating a task file', workspace_create_pdf: 'Creating a PDF',
    workspace_create_svg: 'Creating a graphic', workspace_execute_code: 'Running task code',
    workspace_create_chart: 'Creating a chart',
    workspace_publish: 'Preparing a download', workspace_publish_artifact: 'Preparing a download',
    web_search: 'Searching the web', search: 'Searching connected information',
    // Anthropic code execution on an attached file (the server tool's documented sub-tool names, and the tool itself).
    bash_code_execution: 'Running code on your file', text_editor_code_execution: 'Working with your file in the code sandbox',
    code_execution: 'Running code',
    // Gyro's own app (desktop): "change your outfit" lists the person's looks, then the picker shows under the reply.
    gyro_list_looks: 'Checking my outfits',
};
/**
 * Fallback connector names, used only when the server sent none (an older gyro-svc, or a desktop agent that does
 * not relay the field yet). Keyed by a part of a namespaced tool name ('asana.x', 'mcp__remote_nebula__x').
 */
const CONNECTOR_LABELS = {
    asana: 'Asana', 'asana-helper': 'Asana', bluebeam: 'Bluebeam', revu: 'Bluebeam', graph: 'Microsoft 365',
    m365: 'Microsoft 365', outlook: 'Outlook', sharepoint: 'SharePoint', onedrive: 'OneDrive', teams: 'Teams',
    dropbox: 'Dropbox', github: 'GitHub', procore: 'Procore', autodesk: 'Autodesk', acc: 'Autodesk', aps: 'Autodesk',
    nebula: 'Nebula', orbit: 'Orbit', bridge: 'The Bridge', 'all-hands': 'All Hands', meridian: 'Meridian',
    'the-lab': 'The Lab', lab: 'The Lab', hub: 'The Hub', 'hub-web': 'The Hub', 'hub-ios': 'The Hub', 'hub-android': 'The Hub',
};
/** The first `_` token of a BARE tool name that names its connector ('m365_mail_search'). Deliberately short. */
const PREFIX_CONNECTORS = { m365: 'Microsoft 365', outlook: 'Outlook', revu: 'Bluebeam', lab: 'The Lab' };
const TOOL_NAME = /^[a-zA-Z0-9_.:-]{1,160}$/;
/** What a server-sent connector label may be: letters, digits, spaces and a little punctuation, 1 to 40 characters. */
const CONNECTOR_LABEL = /^[A-Za-z0-9][A-Za-z0-9 .&'()+-]{0,39}$/;
/** The closed set of failure reasons gyro-svc sends on a failed tool_result (gyro-core TOOL_FAILURE_REASONS). */
export const TOOL_FAILURE_REASONS = [
    'connector_contract', 'not_linked', 'consent_required', 'signin_required', 'needs_update', 'refused',
    'not_found', 'rate_limited', 'timeout', 'too_large', 'unavailable', 'failed',
];
/** A ToolProgress exactly: two safe non-negative integers under 10,000; anything else is none. */
export function parseToolProgress(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const { stepsRemaining, callsRemaining } = value;
    const count = (n) => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 && n < 10_000;
    return count(stepsRemaining) && count(callsRemaining) ? { stepsRemaining, callsRemaining } : undefined;
}
/**
 * `{c}` is the connector name, or "this tool" when there is none. Fixed sentences: no server or tool text. The
 * not_linked sentence says only that the connector is not connected: who connects it differs per connector (the
 * Microsoft 365 connector needs an administrator; Asana Helper gives the user a link in the chat), and the chat text
 * carries that. A sentence here must never contradict it.
 */
const FAILURE_TEXT = {
    connector_contract: { state: 'Reply not accepted', sentence: '{c} sent a reply Gyro could not verify, so it was not used. IT needs to fix this; asking again will not help.' },
    not_linked: { state: 'Not connected', sentence: '{c} is not connected for your account yet.' },
    consent_required: { state: 'Needs IT approval', sentence: "IT needs to approve Gyro's access to {c} before this works." },
    signin_required: { state: 'Sign-in needed', sentence: 'Sign in to {c} again, then ask again.' },
    needs_update: { state: 'Update needed', sentence: 'This needs a newer version of the Gyro desktop app. The update installs itself.' },
    refused: { state: 'Not allowed', sentence: '{c} did not allow this for your account.' },
    not_found: { state: 'Not found', sentence: '{c} could not find that item. It may have been moved or deleted.' },
    rate_limited: { state: 'Busy', sentence: '{c} is limiting requests right now. Ask again in a minute.' },
    timeout: { state: 'Timed out', sentence: '{c} took too long to answer. Ask again.' },
    too_large: { state: 'Too much data', sentence: 'The result from {c} was too large to use. Ask for something narrower.' },
    unavailable: { state: 'Unavailable', sentence: '{c} could not be reached right now. Ask again shortly.' },
    failed: { state: 'Failed', sentence: 'This step did not work.' },
};
/** Anthropic code execution's server tools (the documented sub-tool names, and the tool itself). */
const CODE_EXECUTION_TOOLS = new Set(['bash_code_execution', 'text_editor_code_execution', 'code_execution']);
/**
 * Code execution runs on copies in Gyro's code sandbox, never on the user's own files, so where the general sentence
 * would point at the user's file ("may have been moved or deleted") it has its own. The short state stays the same.
 */
const CODE_EXECUTION_FAILURE_SENTENCE = {
    not_found: "The code looked for a file that is not in Gyro's code sandbox. Your own files are not affected.",
};
function isFailureReason(value) {
    return typeof value === 'string' && Object.hasOwn(FAILURE_TEXT, value);
}
function validConnector(value) {
    return typeof value === 'string' && CONNECTOR_LABEL.test(value) ? value : undefined;
}
/**
 * Read the optional fields of a tool event. Anything malformed is ignored (a connector that is not a short plain
 * label) or, for the reason on a FAILED result, becomes the generic 'failed'. A reason on a successful result is
 * dropped: it describes a failure.
 */
export function parseToolEventMeta(data, failed = false) {
    const source = data && typeof data === 'object' ? data : {};
    const connector = validConnector(Object.hasOwn(source, 'connector') ? source.connector : undefined);
    const raw = Object.hasOwn(source, 'reason') ? source.reason : undefined;
    const reason = !failed ? undefined : isFailureReason(raw) ? raw : raw === undefined ? undefined : 'failed';
    const connect = parsePersonConnect(Object.hasOwn(source, 'connect') ? source.connect : undefined);
    const progress = failed ? undefined : parseToolProgress(Object.hasOwn(source, 'progress') ? source.progress : undefined);
    return { ...(connector ? { connector } : {}), ...(reason ? { reason } : {}), ...(connect ? { connect } : {}), ...(progress ? { progress } : {}) };
}
function nameParts(tool) {
    return tool.toLowerCase().split(/__|[.:]/).filter(Boolean).map(part => part.replace(/^remote_/, ''));
}
/**
 * The connector a tool row names: the server's label when it sent a valid one, else the widget's own map from the
 * tool name (a namespaced part or a known prefix). Undefined when nothing is known: a bare cross-app name such as
 * `list_my_tasks` is never guessed, because two systems may expose the same name and a wrong name is worse than none.
 */
export function toolConnectorLabel(tool, connector) {
    const sent = validConnector(connector);
    if (sent)
        return sent;
    if (!TOOL_NAME.test(tool))
        return undefined;
    const parts = nameParts(tool);
    const named = parts.find(part => Object.hasOwn(CONNECTOR_LABELS, part));
    if (named)
        return CONNECTOR_LABELS[named];
    const name = parts.at(-1) ?? '';
    const prefix = name.split('_')[0] ?? '';
    return name.includes('_') && Object.hasOwn(PREFIX_CONNECTORS, prefix) ? PREFIX_CONNECTORS[prefix] : undefined;
}
/**
 * What the tool is doing, in a few words. An unknown tool with a connector reads "Working with <connector>". `summary` (the
 * server's sentence for the call) is read only by "Create my PPP"'s tool rows, which name their group (ppp.ts pppToolLabel):
 * the label is then one of that file's fixed sentences, never the summary text.
 */
export function toolActivityLabel(tool, connector, summary) {
    if (!TOOL_NAME.test(tool))
        return validConnector(connector) ? `Working with ${connector}` : 'Using a tool';
    const ppp = pppToolLabel(tool, summary);
    if (ppp)
        return ppp;
    const parts = nameParts(tool);
    const name = parts.at(-1) ?? '';
    if (Object.hasOwn(TOOL_LABELS, name))
        return TOOL_LABELS[name];
    const sent = validConnector(connector);
    if (sent)
        return `Working with ${sent}`;
    const named = parts.find(part => Object.hasOwn(CONNECTOR_LABELS, part));
    if (named)
        return `Working with ${CONNECTOR_LABELS[named]}`;
    const prefix = name.split('_')[0] ?? '';
    return name.includes('_') && Object.hasOwn(PREFIX_CONNECTORS, prefix) ? `Working with ${PREFIX_CONNECTORS[prefix]}` : 'Using a tool';
}
/** The short state for a failure reason ("Reply not accepted"). Unknown or missing reasons read "Failed". */
export function toolFailureState(reason) {
    return FAILURE_TEXT[isFailureReason(reason) ? reason : 'failed'].state;
}
/** The fixed sentence for a failure reason, naming the connector when there is one ("this tool" otherwise). */
export function toolFailureText(reason, connector) {
    const sentence = FAILURE_TEXT[isFailureReason(reason) ? reason : 'failed'].sentence;
    const name = validConnector(connector);
    // The placeholder can sit mid-sentence ("...access to {c} before..."), so the fallback is capitalised by position.
    return sentence.replace('{c}', name ?? (sentence.startsWith('{c}') ? 'This tool' : 'this tool'));
}
/**
 * Everything a tool row shows, as plain data, so the strip and the task panel say the same thing and the wording is
 * testable without a DOM. `awaitingResult` is the strip's "text arrived after this tool started" state.
 */
export function describeToolActivity(entry, options = {}) {
    const name = toolConnectorLabel(entry.tool, entry.connector);
    const label = toolActivityLabel(entry.tool, entry.connector, entry.summary);
    const failed = entry.status === 'failure';
    const state = failed
        ? toolFailureState(entry.reason)
        : entry.status === 'running'
            ? options.awaitingResult ? 'Awaiting result' : 'Running'
            : entry.status === 'success' ? 'Done' : options.interruptedLabel ?? 'Stopped';
    // A reason is required for the sentence: without one (an older server or agent) the row stays a bare "Failed". A code
    // execution failure uses its own sentence where it has one (the sandbox is not the user's computer).
    const codeSentence = failed && CODE_EXECUTION_TOOLS.has(entry.tool) && isFailureReason(entry.reason) ? CODE_EXECUTION_FAILURE_SENTENCE[entry.reason] : undefined;
    const detail = failed && entry.reason !== undefined ? codeSentence ?? toolFailureText(entry.reason, name) : undefined;
    // Do not repeat the connector on line 2 when line 1 already names it ("Working with Nebula").
    const connector = name && !label.includes(name) ? name : undefined;
    const prefixFailed = failed && state !== 'Failed';
    const spoken = [label, connector, prefixFailed ? `Failed: ${state}` : state, detail]
        .filter((part) => Boolean(part))
        .map(part => (/[.!?]$/.test(part) ? part : `${part}.`))
        .join(' ');
    return { label, ...(connector ? { connector } : {}), state, ...(detail ? { detail } : {}), failed, prefixFailed, spoken };
}
//# sourceMappingURL=toolActivityLabels.js.map